import { useEffect, useState } from "react";
import { useSite } from "../context/SiteContext";
import { bust } from "../lib/imgUrl";
import GalleryOfCta from "../components/GalleryOfCta";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { api } from "../lib/api";
import { trackWa, trackOf } from "../lib/analytics";
import { KISS } from "../lib/wa";

const VIP_STORAGE_KEY = "camilla_vip_ok";

// Server keeps the VIP images list based on filenames. The public content
// endpoint doesn't expose the VIP list separately — we use fixed slot names.
const PUBLIC = [1, 2, 3, 4, 5, 6].map((n) => ({
  src: `/gallery/gallery-0${n}.jpg`,
  alt: `Gallery ${n}`,
  slot: `gallery-0${n}`,
}));
const VIP = [1, 2, 3].map((n) => ({
  src: `/gallery/vip-0${n}.jpg`,
  alt: `VIP ${n}`,
  slot: `vip-0${n}`,
}));

function Tile({ src, alt, onClick, idx, testPrefix }) {
  return (
    <button
      className="wm-wrap block w-full rounded-xl overflow-hidden border border-[rgba(201,169,97,0.12)] focus:outline-none focus:ring-2 focus:ring-[color:var(--gold)]"
      onClick={onClick}
      data-testid={`${testPrefix}-${idx}`}
    >
      <img
        src={src}
        alt={alt}
        className="w-full h-auto object-cover no-drag"
        loading="lazy"
        onContextMenu={(e) => e.preventDefault()}
        draggable={false}
      />
    </button>
  );
}

export default function Gallery() {
  const { content, versions } = useSite();
  const [vipUnlocked, setVipUnlocked] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [pwValue, setPwValue] = useState("");
  const [lightbox, setLightbox] = useState({ open: false, images: [], index: 0 });

  const ofUrl =
    content.onlyfans_url ||
    content.onlyfans ||
    "https://onlyfans.com/camilla.xxx?rec=485728775";
  const waNumber = content.contact?.wa_number || "61437780132";
  const waVipUrl = `https://wa.me/${waNumber}?text=${encodeURIComponent(
    `Hi Camilla ${KISS} how do I get VIP access?`
  )}`;

  const onVipDm = () => {
    trackWa("vip_modal_dm");
    window.open(waVipUrl, "_blank", "noopener,noreferrer");
  };
  const onVipOf = () => {
    trackOf("vip_modal_of");
    window.open(ofUrl, "_blank", "noopener,noreferrer");
  };

  useEffect(() => {
    setVipUnlocked(window.localStorage.getItem(VIP_STORAGE_KEY) === "1");
  }, []);

  const publicImgs = PUBLIC.map((i) => ({ ...i, src: bust(i.src, versions) }));
  const vipImgs = VIP.map((i) => ({ ...i, src: bust(i.src, versions) }));
  const combined = vipUnlocked ? [...publicImgs, ...vipImgs] : publicImgs;

  const openLightbox = (images, index) =>
    setLightbox({ open: true, images, index });
  const closeLightbox = () => setLightbox((l) => ({ ...l, open: false }));
  const prev = () =>
    setLightbox((l) => ({
      ...l,
      index: (l.index - 1 + l.images.length) % l.images.length,
    }));
  const next = () =>
    setLightbox((l) => ({ ...l, index: (l.index + 1) % l.images.length }));

  useEffect(() => {
    if (!lightbox.open) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightbox.open]);

  const submitPw = async (e) => {
    e.preventDefault();
    try {
      const r = await api.vipCheck(pwValue.trim());
      if (r.ok) {
        window.localStorage.setItem(VIP_STORAGE_KEY, "1");
        setVipUnlocked(true);
        setPwOpen(false);
        setPwValue("");
        toast.success("VIP unlocked. Have fun 💋");
      } else {
        toast.error("Wrong password. Try again or ask on WhatsApp.");
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <section id="gallery" className="section" data-testid="gallery-section">
      <div className="container-x px-5 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <div className="eyebrow mb-4">Gallery</div>
            <h2 className="font-display text-4xl md:text-6xl text-gold leading-tight">
              A little tease.
            </h2>
            <p className="text-ivory-dim mt-3 max-w-lg">
              A curated look. Face-blurred by design — privacy is part of the
              experience.
            </p>
          </div>
          {!vipUnlocked ? (
            <div className="flex flex-col self-start md:self-end md:items-end gap-2">
              <button
                className="btn-ghost-gold"
                onClick={() => setPwOpen(true)}
                data-testid="unlock-vip-btn"
              >
                🔒 Unlock VIP Gallery
              </button>
              <p
                className="text-[12px] md:text-right max-w-[280px] leading-snug"
                style={{ color: "var(--gold-soft)", opacity: 0.85 }}
                data-testid="vip-invite-caption"
              >
                VIP access is for booked clients & my VIPs — DM me on WhatsApp for a code 💋
              </p>
            </div>
          ) : (
            <span
              className="pill self-start md:self-end"
              data-testid="vip-unlocked-badge"
            >
              VIP Unlocked
            </span>
          )}
        </div>

        <div className="masonry" data-testid="gallery-grid">
          {publicImgs.map((img, i) => (
            <Tile
              key={img.src}
              src={img.src}
              alt={img.alt}
              onClick={() => openLightbox(combined, i)}
              idx={i}
              testPrefix="gallery-item"
            />
          ))}
          <GalleryOfCta variant="public" />
          {vipUnlocked &&
            vipImgs.map((img, i) => (
              <Tile
                key={img.src}
                src={img.src}
                alt={img.alt}
                onClick={() => openLightbox(combined, publicImgs.length + i)}
                idx={i}
                testPrefix="vip-gallery-item"
              />
            ))}
          {vipUnlocked && <GalleryOfCta variant="vip" />}
        </div>
      </div>

      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent
          className="bg-[#0f0d0c] border border-[rgba(201,169,97,0.25)] text-ivory sm:max-w-md"
          data-testid="vip-password-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-3xl text-gold">
              🔒 VIP Gallery
            </DialogTitle>
            <DialogDescription className="text-ivory-dim italic">
              Exclusive photos — for booked clients & my VIPs.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitPw} className="mt-4 space-y-3">
            <input
              type="password"
              className="input-luxe"
              placeholder="Enter your code or password"
              value={pwValue}
              onChange={(e) => setPwValue(e.target.value)}
              autoFocus
              data-testid="vip-password-input"
            />
            <button
              type="submit"
              className="btn-gold w-full"
              data-testid="vip-password-submit"
            >
              Unlock 💋
            </button>
          </form>

          <div className="mt-6 pt-5 border-t border-[rgba(201,169,97,0.18)]">
            <p className="text-center text-[11px] uppercase tracking-[0.22em] text-[color:var(--gold-soft)] mb-4">
              Don't have a code yet?
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={onVipDm}
                className="vip-alt-btn vip-alt-btn--gold"
                data-testid="vip-modal-wa-btn"
              >
                <span className="vip-alt-btn__icon" aria-hidden>💬</span>
                <span className="vip-alt-btn__label">Message me on WhatsApp</span>
                <span className="vip-alt-btn__cap">I'll send you a code personally</span>
              </button>
              <button
                type="button"
                onClick={onVipOf}
                className="vip-alt-btn vip-alt-btn--wine"
                data-testid="vip-modal-of-btn"
              >
                <span className="vip-alt-btn__icon" aria-hidden>🔥</span>
                <span className="vip-alt-btn__label">See everything on OnlyFans</span>
                <span className="vip-alt-btn__cap">Daily uncensored content</span>
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {lightbox.open && (
        <div
          className="fixed inset-0 z-[60] lb-overlay flex items-center justify-center p-4"
          onClick={closeLightbox}
          data-testid="lightbox"
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              prev();
            }}
            className="absolute left-4 md:left-8 text-gold text-4xl hover:text-ivory transition-colors"
            aria-label="Previous"
            data-testid="lightbox-prev"
          >
            ‹
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
            className="absolute right-4 md:right-8 text-gold text-4xl hover:text-ivory transition-colors"
            aria-label="Next"
            data-testid="lightbox-next"
          >
            ›
          </button>
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-gold text-2xl hover:text-ivory"
            aria-label="Close"
            data-testid="lightbox-close"
          >
            ×
          </button>
          <div
            className="wm-wrap max-h-[90vh] max-w-[92vw]"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={lightbox.images[lightbox.index]?.src}
              alt={lightbox.images[lightbox.index]?.alt}
              className="max-h-[90vh] max-w-[92vw] object-contain rounded"
              onContextMenu={(e) => e.preventDefault()}
              draggable={false}
              data-testid="lightbox-image"
            />
          </div>
        </div>
      )}
    </section>
  );
}
